import { useEffect, useRef, useState } from "react";
import { ArrowUp, RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ReaderAiReferenceChip } from "@/components/ui/reader-ai/reference-chip";
import { ReaderPlusMenu } from "@/components/ui/reader-ai/reader-plus-menu";
import { SelectionActivationCue } from "@/components/ui/reader-ai/selection-activation-cue";
import { ToofiInfoCue } from "@/components/ui/reader-ai/toofi-info-cue";
import type { ReaderAiModeSelection } from "@/components/ui/reader-ai/types";
import { Textarea } from "@/components/ui/textarea";
import { getCompactToofiModePlaceholder, getToofiHomePrompt, getToofiModeMeta } from "@/lib/toofi-modes";
import { cn } from "@/lib/utils";
import type { ReaderAiActiveReference, ToofiMemoryMode, ToofiProductIntent, ToofiProductMode } from "@/types/agentic-chat";

const MAX_PROMPT_LENGTH = 1200;

type ReaderAiPromptComposerProps = {
  variant?: "home" | "thread";
  disabled?: boolean;
  onSubmit: (prompt: string, selection?: ReaderAiModeSelection | null) => void;
  embedded?: boolean;
  viewport?: "phone" | "tablet" | "desktop";
  surfaceKind?: "reader" | "workspace";
  draft?: string;
  onDraftChange?: (draft: string) => void;
  contextLabel?: string;
  activeReference?: ReaderAiActiveReference | null;
  helperText?: string;
  placeholder?: string;
  onClearContext?: () => void;
  selectionActivationMode?: "text" | "visual" | null;
  onSelectTextContext?: () => void;
  onUseVisualContext?: () => void;
  warningLevel?: "none" | "soft" | "strong";
  onStartNewChat?: () => void;
  productMode?: ToofiProductMode | null;
  onProductModeChange?: (mode: ToofiProductMode | null) => void;
  memoryMode?: ToofiMemoryMode;
  onMemoryModeChange?: (mode: ToofiMemoryMode) => void;
  showModeMenu?: boolean;
  showMemoryControls?: boolean;
};

export function ReaderAiPromptComposer({
  variant = "thread",
  disabled = false,
  onSubmit,
  embedded = false,
  viewport = "desktop",
  surfaceKind = "reader",
  draft,
  onDraftChange,
  contextLabel,
  activeReference,
  helperText,
  placeholder,
  onClearContext,
  selectionActivationMode = null,
  onSelectTextContext,
  onUseVisualContext,
  warningLevel = "none",
  onStartNewChat,
  productMode,
  onProductModeChange,
  memoryMode = "default",
  onMemoryModeChange,
  showModeMenu: showModeMenuProp,
  showMemoryControls,
}: ReaderAiPromptComposerProps) {
  const [internalDraft, setInternalDraft] = useState("");
  const [isComposing, setIsComposing] = useState(false);
  const [selectedIntent, setSelectedIntent] = useState<ToofiProductIntent | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const controlled = typeof draft === "string";
  const value = controlled ? draft : internalDraft;
  const remaining = MAX_PROMPT_LENGTH - value.length;
  const isPhone = viewport === "phone";
  const isTablet = viewport === "tablet";
  const isHomeVariant = variant === "home";
  const maxTextareaHeight = isPhone ? 144 : isTablet ? 160 : 176;
  const resolvedIntent = productMode === "learn" ? selectedIntent : null;
  const activeModeLabel = productMode ? getToofiModeMeta(productMode).label : null;
  const showContextInfoCue = Boolean(
    helperText &&
      activeReference &&
      (activeReference.kind === "file" || activeReference.kind === "visual"),
  );
  const visibleHelperText = showContextInfoCue ? null : helperText;
  const hasContext = Boolean(contextLabel || activeReference?.kind !== "none");
  const resolvedPlaceholder =
    placeholder ??
    (() => {
      if (embedded && !isHomeVariant) return "";

      if (isHomeVariant) {
        return getToofiHomePrompt({
          mode: productMode ?? null,
          surfaceKind,
          hasContext,
          stepByStep: productMode === "learn" && resolvedIntent === "step_by_step",
        });
      }

      if (productMode === "learn" && resolvedIntent === "step_by_step") {
        return "Teach this step by step";
      }

      if (productMode) {
        return getCompactToofiModePlaceholder({
          mode: productMode,
          surfaceKind,
          hasContext,
        });
      }

      if (hasContext) {
        return surfaceKind === "workspace" ? "Ask about this context" : "Ask about this selection";
      }

      return surfaceKind === "workspace" ? "Ask about your study" : "Ask about this file";
    })();

  useEffect(() => {
    if (productMode !== "learn" && selectedIntent) {
      setSelectedIntent(null);
    }
  }, [productMode, selectedIntent]);

  useEffect(() => {
    const node = textareaRef.current;
    if (!node) return;

    if (isHomeVariant) {
      node.style.height = isPhone ? "32px" : "34px";
      return;
    }

    node.style.height = "0px";
    node.style.height = `${Math.min(node.scrollHeight, maxTextareaHeight)}px`;
  }, [isHomeVariant, isPhone, maxTextareaHeight, value]);

  const setDraft = (next: string) => {
    const clipped = next.slice(0, MAX_PROMPT_LENGTH);
    if (!controlled) {
      setInternalDraft(clipped);
    }
    onDraftChange?.(clipped);
  };

  const handleSubmit = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSubmit(trimmed, {
      productMode: productMode ?? null,
      productIntent: resolvedIntent,
    });
    setDraft("");
  };

  const warningCopy =
    warningLevel === "strong"
      ? "This chat now spans several topics. A fresh chat may keep Toofi cleaner and more focused."
      : warningLevel === "soft"
      ? "This chat is getting long. A fresh chat can help Toofi stay focused."
      : null;
  const showModeMenu = showModeMenuProp ?? Boolean(onProductModeChange);
  const showMemoryToggle = showMemoryControls ?? !isHomeVariant;
  const showModeBanner = Boolean(productMode) && !isHomeVariant;

  return (
    <div
      className={cn(
        "animate-in fade-in-0 slide-in-from-bottom-2 duration-200 w-full",
        isHomeVariant
          ? "p-0"
          : embedded
          ? ""
          : isTablet
            ? "rounded-[1.5rem] border border-white/70 bg-[linear-gradient(180deg,rgba(255,255,255,0.95),rgba(241,245,249,0.92))] p-2.5 backdrop-blur-2xl shadow-[0_24px_60px_rgba(148,163,184,0.16)]"
            : "rounded-[1.6rem] border border-white/70 bg-[linear-gradient(180deg,rgba(255,255,255,0.95),rgba(241,245,249,0.92))] p-3 backdrop-blur-2xl shadow-[0_24px_60px_rgba(148,163,184,0.18)]",
      )}
    >
      {warningCopy && !isHomeVariant ? (
        <div
          className={cn(
            "mb-2 flex items-center justify-between gap-2 rounded-[0.95rem] border px-3 py-1.5 text-[11px] sm:mb-2",
            isPhone && "mb-1 rounded-[0.8rem] px-2 py-1 text-[10px] leading-4",
            warningLevel === "strong"
              ? "border-amber-200/90 bg-amber-50/65 text-amber-900"
              : "border-slate-200/80 bg-white/55 text-slate-600",
          )}
        >
          <span className="min-w-0 flex-1">{warningCopy}</span>
          {warningLevel === "strong" && onStartNewChat ? (
            <Button
              type="button"
              variant="ghost"
              className="h-7 rounded-full px-2.5 text-[11px] text-amber-900 hover:bg-amber-100/60 active:scale-95 transition-all duration-150 touch-manipulation"
              onClick={onStartNewChat}
            >
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              New chat
            </Button>
          ) : null}
        </div>
      ) : null}
      {showModeBanner ? (
        <div className={cn("mb-2 flex flex-wrap items-center gap-2 sm:mb-2", isPhone && "mb-1 gap-1.5")}>
          <span
            className={cn(
              "inline-flex items-center rounded-full border border-blue-100/80 bg-blue-50/80 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-blue-700",
              isPhone && "px-2 py-0.5 text-[9px]",
            )}
          >
            {getToofiModeMeta(productMode).label}
          </span>
          {showMemoryToggle && onMemoryModeChange ? (
            <button
              type="button"
              onClick={() => onMemoryModeChange(memoryMode === "temporary" ? "default" : "temporary")}
              className={cn(
                "inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] transition-colors",
                memoryMode === "temporary"
                  ? "border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100"
                  : "border-slate-200 bg-white text-slate-500 hover:bg-slate-100 hover:text-slate-700",
                isPhone && "px-2 py-0.5 text-[9px]",
              )}
            >
              {memoryMode === "temporary" ? "Temporary chat" : "Use memory"}
            </button>
          ) : null}
          {showMemoryToggle && onMemoryModeChange ? (
            <ToofiInfoCue
              label="Memory"
              explanation={
                memoryMode === "temporary"
                  ? "Temporary chat keeps this thread and page context, but skips durable Toofi memory."
                  : "Use memory lets Toofi use durable study memory, mastery signals, and your profile context when relevant."
              }
              className={cn(isPhone && "h-4 w-4")}
            />
          ) : null}
        </div>
      ) : null}
      {activeReference || contextLabel ? (
        <div className={cn("mb-2 flex flex-wrap items-center gap-2 sm:mb-2", isPhone && "mb-1 gap-1.5")}>
          <ReaderAiReferenceChip
            reference={activeReference ?? { kind: "selection", label: contextLabel ?? "Selected text", pageNumber: 0 }}
            compact={isPhone}
            onClear={onClearContext}
          />
          {showContextInfoCue ? (
            <ToofiInfoCue
              label="Context info"
              explanation={helperText ?? ""}
              className={cn(isPhone && "h-4 w-4")}
            />
          ) : null}
        </div>
      ) : null}
      {selectionActivationMode ? (
        <div className={cn("mb-2 px-1", isPhone && "mb-1")}>
          <SelectionActivationCue mode={selectionActivationMode} compact={isPhone} />
        </div>
      ) : null}
      {visibleHelperText && !isHomeVariant ? (
        <p className={cn("mb-2 px-1 text-slate-500", isPhone ? "text-[10px] leading-4" : "text-[11px] leading-5")}>
          {visibleHelperText}
        </p>
      ) : null}
      <div
        className={cn(
          "flex items-end gap-2 rounded-[1.5rem] border border-slate-200/75 bg-white/78 p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.72),0_12px_26px_rgba(148,163,184,0.08)] backdrop-blur-md transition-all",
          isHomeVariant &&
            "h-[52px] items-center gap-1 overflow-hidden rounded-full border-white/85 bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(248,250,252,0.9))] px-2 py-1 shadow-[0_14px_34px_rgba(148,163,184,0.12),inset_0_1px_0_rgba(255,255,255,0.82)] backdrop-blur-2xl",
          embedded && (isPhone || isTablet)
            ? isTablet
              ? "rounded-[1.45rem] bg-white/74 p-2.5"
              : "rounded-[1.55rem] bg-white/72"
            : isTablet
              ? "rounded-[1.45rem] bg-[linear-gradient(180deg,rgba(255,255,255,0.86),rgba(248,250,252,0.74))] p-2.5"
              : "rounded-[1.4rem] bg-[linear-gradient(180deg,rgba(255,255,255,0.88),rgba(248,250,252,0.76))] p-2.5",
          embedded && !isPhone && "gap-1 rounded-[1.2rem] bg-white/72 p-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.72),0_8px_18px_rgba(148,163,184,0.08)]",
          isPhone && "gap-1 rounded-[1.2rem] p-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.72),0_8px_18px_rgba(148,163,184,0.08)]",
        )}
        >
        {showModeMenu && onProductModeChange ? (
          <ReaderPlusMenu
            surfaceKind={surfaceKind}
            value={productMode ?? null}
            onModeChange={onProductModeChange}
            selectedIntent={resolvedIntent}
            onIntentSelect={setSelectedIntent}
            onSelectText={surfaceKind === "reader" ? onSelectTextContext : undefined}
            onSelectVisual={surfaceKind === "reader" ? onUseVisualContext : undefined}
            side={isHomeVariant ? "bottom" : "top"}
            compact={isPhone || isHomeVariant}
            className={cn(isHomeVariant ? "h-7 w-7" : !isPhone && "mb-1")}
          />
        ) : null}
        {isHomeVariant ? (
          <input
            value={value}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.nativeEvent.isComposing || isComposing) return;
              if (event.key === "Enter") {
                event.preventDefault();
                handleSubmit();
              }
            }}
            onCompositionStart={() => setIsComposing(true)}
            onCompositionEnd={() => setIsComposing(false)}
            disabled={disabled}
            spellCheck={false}
            autoCorrect="off"
            maxLength={MAX_PROMPT_LENGTH}
            placeholder={resolvedPlaceholder}
            className={cn(
              "h-[34px] min-w-0 flex-1 border-0 bg-transparent px-1 text-[13px] text-slate-900 outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 placeholder:text-[12px] placeholder:text-slate-400",
              isPhone ? "placeholder:tracking-[-0.01em]" : null,
            )}
            aria-label={resolvedPlaceholder || "Message Toofi"}
          />
        ) : (
          <Textarea
            ref={textareaRef}
            value={value}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.nativeEvent.isComposing || isComposing) return;
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                handleSubmit();
              }
            }}
            onCompositionStart={() => setIsComposing(true)}
            onCompositionEnd={() => setIsComposing(false)}
            disabled={disabled}
            spellCheck={false}
            autoCorrect="off"
            rows={1}
            placeholder={resolvedPlaceholder}
            className={cn(
              "min-h-[56px] min-w-0 flex-1 resize-none overflow-y-auto !border-0 !bg-transparent px-3 py-2.5 text-[16px] leading-6 text-slate-900 !shadow-none focus-visible:!ring-0 focus-visible:!ring-offset-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
              embedded && (isPhone || isTablet)
                ? "placeholder:text-[13px] placeholder:text-slate-500 sm:min-h-[54px]"
                : "placeholder:text-[14px] placeholder:text-slate-400 sm:min-h-[54px] lg:min-h-[56px]",
              embedded && !isPhone ? "min-h-[42px] px-1.5 py-1.5 text-[14px] leading-5 placeholder:text-[13px] placeholder:text-slate-500" : null,
              isPhone ? "min-h-[46px] text-[16px] leading-5" : isTablet ? "min-h-[52px] text-[15px] leading-6" : "text-[15px] leading-6 xl:text-[15px]",
              isPhone ? "placeholder:tracking-[-0.01em]" : null,
              embedded ? "px-1.5 py-1.5" : isPhone ? "px-1.5 py-1.5" : isTablet ? "px-2 py-2" : "px-2.5 py-2.5",
            )}
            aria-label={resolvedPlaceholder || "Message Toofi"}
          />
        )}
        <Button
          type="button"
          size="icon"
          disabled={disabled || !value.trim()}
          className={cn(
            "mb-1 shrink-0 rounded-full border border-white/70 bg-[linear-gradient(180deg,rgba(59,130,246,0.96),rgba(37,99,235,0.92))] text-primary-foreground shadow-[0_16px_36px_rgba(37,99,235,0.35)] hover:-translate-y-0.5 hover:bg-primary/90 disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-300 active:scale-95 transition-all duration-150 touch-manipulation",
            isHomeVariant ? "mb-0 h-8 w-8" : embedded ? "h-8 w-8" : isPhone ? "h-8 w-8" : isTablet ? "h-9 w-9" : "h-10 w-10",
          )}
          onClick={handleSubmit}
        >
          <ArrowUp className={cn(isHomeVariant ? "h-3.5 w-3.5" : "h-4 w-4")} />
          <span className="sr-only">Send prompt</span>
        </Button>
      </div>
      {!embedded && !isHomeVariant ? (
        <div className="mt-1.5 flex items-center justify-between px-1">
          <p className="text-[10px] text-slate-400">Enter to send. Shift+Enter for a new line.</p>
          <p className="text-[10px] text-slate-400">{remaining}</p>
        </div>
      ) : null}
    </div>
  );
}
